'use client';

import type React from 'react';
import { useRef } from 'react';
import type * as HighchartsTypes from 'highcharts';
import dynamic from 'next/dynamic';
import { useThemeMode } from '@terra/ui-design-system';
import { token } from '@terra/ui-styled-system';
import {
  readSankeyNodeLayouts,
  type SankeyNodeLayout,
} from './sankey-node-layout';

type SankeyTooltipStyles = {
  background: string;
  color: string;
};

type SankeyNodeContext = {
  id?: string;
  name?: string;
  sum?: number;
  column?: number;
  linksTo?: SankeyLinkContext[];
  options?: { id?: string; name?: string };
};

type SankeyLinkContext = {
  weight?: number;
  fromNode?: SankeyNodeContext;
  toNode?: SankeyNodeContext;
};

function getSankeyTooltipStyles(
  theme: 'light' | 'dark' = 'light',
): SankeyTooltipStyles {
  if (theme === 'dark') {
    return {
      background: token('colors.white'),
      color: token('colors.neutral.light.12'),
    };
  }

  return {
    background: token('colors.neutral.light.12'),
    color: token('colors.white'),
  };
}

function wrapSankeyTooltipHtml({
  content,
  styles,
}: {
  content: string;
  styles: SankeyTooltipStyles;
}): string {
  return `<div style="color: ${styles.color}; font-family: inherit; font-size: 12px; line-height: 1.5;">${content}</div>`;
}

const ChartComponent = dynamic(
  async () => {
    const HighchartsModule = await import('highcharts');

    await import('highcharts/modules/sankey');
    await import('highcharts/modules/accessibility');
    const { default: HighchartsReact } = await import(
      'highcharts-react-official'
    );

    return ({ options }: { options: HighchartsTypes.Options }) => {
      const Highcharts =
        (HighchartsModule as { default?: typeof HighchartsTypes }).default ??
        HighchartsModule;

      return <HighchartsReact highcharts={Highcharts} options={options} />;
    };
  },
  {
    ssr: false,
    loading: () => null,
  },
) as React.ComponentType<{ options: HighchartsTypes.Options }>;

type SankeyDataPoint = [from: string, to: string, weight: number];

type SankeyNode = {
  id: string;
  color: string;
  visible?: boolean;
  name: string;
  column?: number;
};

type SankeyPointClickContext = {
  isNode?: boolean;
  id?: string;
  from?: string;
  to?: string;
  options?: { id?: string };
  point?: SankeyPointClickContext;
};

export type SankeyChartProps = {
  data: SankeyDataPoint[];
  nodes: SankeyNode[];
  height?: number | string;
  tooltipBackgroundColor?: string;
  tooltipTextColor?: string;
  chartMarginLeft?: number;
  chartMarginRight?: number;
  chartMarginTop?: number;
  chartMarginBottom?: number;
  nodeWidth?: number;
  nodeLabelFormatter?: (node: SankeyNodeContext) => string;
  nodeTooltipFormatter?: (node: SankeyNodeContext) => string;
  pointTooltipFormatter?: (point: SankeyLinkContext) => string;
  onNodeClick?: (nodeId: string) => void;
  onLinkClick?: (link: { from: string; to: string }) => void;
  onNodeLayouts?: (layouts: SankeyNodeLayout[]) => void;
};

const SANKEY_LINK_OPACITY = 0.5;
const DEFAULT_SANKEY_CHART_MARGIN_RIGHT = 168;

export function SankeyChart({
  data,
  nodes,
  height,
  tooltipBackgroundColor,
  tooltipTextColor,
  chartMarginLeft,
  chartMarginRight,
  chartMarginTop,
  chartMarginBottom,
  nodeWidth = 20,
  nodeLabelFormatter,
  nodeTooltipFormatter,
  pointTooltipFormatter,
  onNodeClick,
  onLinkClick,
  onNodeLayouts,
}: SankeyChartProps) {
  const onNodeClickRef = useRef(onNodeClick);

  onNodeClickRef.current = onNodeClick;
  const onLinkClickRef = useRef(onLinkClick);

  onLinkClickRef.current = onLinkClick;
  const hasClickHandlers = Boolean(onNodeClick || onLinkClick);
  const onNodeLayoutsRef = useRef(onNodeLayouts);

  onNodeLayoutsRef.current = onNodeLayouts;

  const resolveNodeId = (point: SankeyPointClickContext): string | undefined =>
    point.id ?? point.options?.id;
  const handlePointClick = function (this: SankeyPointClickContext): void {
    const point = this.point ?? this;

    if (point.isNode) {
      const nodeId = resolveNodeId(point);

      if (nodeId) {
        onNodeClickRef.current?.(nodeId);
      }

      return;
    }

    if (point.from && point.to) {
      onLinkClickRef.current?.({ from: point.from, to: point.to });
    }
  };

  const { mode: themeMode } = useThemeMode();
  const resolvedTooltipStyles = getSankeyTooltipStyles(themeMode ?? 'light');

  const options = {
    title: { text: '' },
    credits: { enabled: false },
    chart: {
      backgroundColor: 'transparent',
      height: height,
      ...(chartMarginLeft !== undefined ? { marginLeft: chartMarginLeft } : {}),
      marginRight: chartMarginRight ?? DEFAULT_SANKEY_CHART_MARGIN_RIGHT,
      ...(chartMarginTop !== undefined ? { marginTop: chartMarginTop } : {}),
      ...(chartMarginBottom !== undefined
        ? { marginBottom: chartMarginBottom }
        : {}),
      style: {
        fontFamily: 'inherit',
      },
      events: {
        render(this: HighchartsTypes.Chart) {
          onNodeLayoutsRef.current?.(readSankeyNodeLayouts(this));
        },
      },
    },
    series: [
      {
        type: 'sankey',
        keys: ['from', 'to', 'weight'],
        nodeWidth,
        borderRadius: 0,
        borderWidth: 2,
        nodePadding: 20,
        borderColor: token('colors.bg.default'),
        linkColorMode: 'gradient',
        minLinkWidth: 17,
        linkOpacity: SANKEY_LINK_OPACITY,
        stickyTracking: false,
        ...(hasClickHandlers
          ? {
              cursor: 'pointer',
              point: { events: { click: handlePointClick } },
            }
          : {}),
        dataLabels: {
          align: 'left',
          padding: 25,
          useHTML: true,
          allowOverlap: true,
          crop: false,
          overflow: 'allow',
          ...(hasClickHandlers
            ? {
                events: {
                  click: handlePointClick,
                },
              }
            : {}),
          ...(nodeLabelFormatter
            ? {
                nodeFormatter: function (this: SankeyNodeContext) {
                  return nodeLabelFormatter(this);
                },
              }
            : { nodeFormat: '{point.sum} {point.name}' }),
          style: {
            color: token('colors.fg.default'),
            fontSize: '11px',
            fontWeight: '400',
            textOutline: 'none',
            cursor: hasClickHandlers ? 'pointer' : undefined,
            pointerEvents: 'auto',
          },
        },
        data,
        nodes,
      },
    ],
    tooltip: {
      backgroundColor:
        tooltipBackgroundColor ?? resolvedTooltipStyles.background,
      borderWidth: 0,
      borderRadius: 4,
      padding: 10,
      shadow: false,
      headerFormat: '',
      useHTML: true,
      followPointer: false,
      pointFormatter: function (this: SankeyLinkContext): string {
        const tooltipStyles: SankeyTooltipStyles = {
          background:
            tooltipBackgroundColor ?? resolvedTooltipStyles.background,
          color: tooltipTextColor ?? resolvedTooltipStyles.color,
        };

        if (pointTooltipFormatter) {
          return wrapSankeyTooltipHtml({
            content: pointTooltipFormatter(this),
            styles: tooltipStyles,
          });
        }

        const from = this.fromNode?.name || '';
        const to = this.toNode?.name || '';
        const value = this.weight ?? 0;
        const originTotal = this.fromNode?.sum ?? 0;
        const percent = originTotal > 0 ? (value / originTotal) * 100 : 0;

        return wrapSankeyTooltipHtml({
          content: `${from} → ${to}<br/><span style="font-weight: 500;">${percent.toFixed(1)}%</span> · ${value}`,
          styles: tooltipStyles,
        });
      },
      nodeFormatter: function (this: SankeyNodeContext): string {
        const tooltipStyles: SankeyTooltipStyles = {
          background:
            tooltipBackgroundColor ?? resolvedTooltipStyles.background,
          color: tooltipTextColor ?? resolvedTooltipStyles.color,
        };

        if (nodeTooltipFormatter) {
          return wrapSankeyTooltipHtml({
            content: nodeTooltipFormatter(this),
            styles: tooltipStyles,
          });
        }

        const value = this.sum ?? 0;
        const name = this.name ?? '';

        const nodeColumn = this.column ?? 0;

        if (nodeColumn > 0) {
          const linksTo = this.linksTo ?? [];
          const prevNodesTotal = linksTo.reduce(
            (sum: number, link: SankeyLinkContext) =>
              sum + (link.fromNode?.sum ?? 0),
            0,
          );
          const percent =
            prevNodesTotal > 0 ? (value / prevNodesTotal) * 100 : 0;

          return wrapSankeyTooltipHtml({
            content: `${name}<br/><span style="font-weight: 500;">${percent.toFixed(1)}%</span> · ${value}`,
            styles: tooltipStyles,
          });
        }

        return wrapSankeyTooltipHtml({
          content: `${name}<br/>${value}`,
          styles: tooltipStyles,
        });
      },
      style: {
        color: tooltipTextColor ?? resolvedTooltipStyles.color,
        fontSize: '12px',
        lineHeight: '1.5',
      },
    },
  };

  return <ChartComponent options={options as HighchartsTypes.Options} />;
}
